import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useDispatch, useSelector, RootStateOrAny } from 'react-redux';
import * as Yup from 'yup';
import { SubmitHandler, FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import {
  FetchClientCodeActions,
  FetchClientCodeState,
  DeleteClientCodeActions,
  DeleteClientCodeState,
  CreateClientCodeActions,
  CreateClientCodeState,
} from 'store/ducks/settings/client-codes';
import { Modal, Alert } from 'components/shared';
import { Input } from 'components/shared/Form';
import { useTranslation, useValidation } from 'hooks';
import { translations } from './translations';
import * as S from './styles';

interface Props {
  clientId: string;
}

export const ClientCodes: React.FC<Props> = ({ clientId }) => {
  const formCodeRef = useRef<FormHandles>(null);
  const dispatch = useDispatch();
  const { getTranslation } = useTranslation(translations);
  const { handleFormErrors } = useValidation();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedCode, setSelectedCode] = useState<number>(0);

  const { data, loading } = useSelector<RootStateOrAny, FetchClientCodeState>(
    (state: any) => state.fetchClientCode
  );
  const { loading: deleteLoading } = useSelector<
    RootStateOrAny,
    DeleteClientCodeState
  >((state: any) => state.deleteClientCode);

  const { loading: createLoading } = useSelector<
    RootStateOrAny,
    CreateClientCodeState
  >((state: any) => state.createClientCode);

  const fetchClientCodes = useCallback(() => {
    dispatch(FetchClientCodeActions.request(clientId));
  }, [clientId, dispatch]);

  const onSuccess = useCallback(() => {
    fetchClientCodes();
    setModalOpen(false);
  }, [fetchClientCodes]);

  const deleteClientCode = useCallback(() => {
    if (selectedCode) {
      dispatch(DeleteClientCodeActions.request(selectedCode, onSuccess));
    }
  }, [selectedCode, dispatch, onSuccess]);

  const handleDelete = useCallback((clientCodeId: number) => {
    setSelectedCode(clientCodeId);
    setModalOpen(true);
  }, []);

  const handleSubmit: SubmitHandler = useCallback(
    async (data: any) => {
      try {
        formCodeRef.current?.setErrors({});
        const schema = Yup.object().shape({
          client_code: Yup.mixed()
            .test('codeValidation', 'Código inválido', (value: any) =>
              Number.isInteger(Math.abs(Number(value)))
            )
            .required('Obrigatório'),
        });
        await schema.validate(data, {
          abortEarly: false,
        });
        data.general_client_id = clientId;
        dispatch(CreateClientCodeActions.request(data, onSuccess));
      } catch (error) {
        handleFormErrors(error, formCodeRef);
      }
    },
    [clientId, dispatch, handleFormErrors, onSuccess]
  );

  useEffect(() => {
    fetchClientCodes();
  }, [fetchClientCodes]);
  return (
    <S.Container>
      <Modal isOpen={modalOpen}>
        <Alert
          title={`Remover Código ERP`}
          text="Ao excluir o código ERP do cliente, você estará impedindo a associação do mesmo na importação de Pickings. Deseja realmente remover?"
          close={() => setModalOpen(false)}
          action={deleteClientCode}
          labelAction="Remover"
          isLoading={deleteLoading}
        />
      </Modal>
      <S.Title>
        {getTranslation('codigoSap')} {loading && <S.Loading />}
      </S.Title>
      <S.Text>{getTranslation('descricao')}</S.Text>
      <S.BoxContainer>
        <Form
          ref={formCodeRef}
          onSubmit={handleSubmit}
          placeholder=""
          onPointerEnterCapture={() => {}}
          onPointerLeaveCapture={() => {}}
        >
          <S.FormRow>
            <Input
              name="client_code"
              placeholder={getTranslation('codigoSap')}
            />
            <S.ButtonMini
              type="button"
              className="add"
              onClick={() => formCodeRef.current?.submitForm()}
            >
              {createLoading ? <S.Loading /> : getTranslation('adicionar')}
            </S.ButtonMini>
          </S.FormRow>
        </Form>
        {data.map((code) => (
          <S.Item key={code.id}>
            <S.ItemLabel>
              <span>{getTranslation('codigo')}: </span>
              {code.client_code}
            </S.ItemLabel>
            <S.DeleteButton onClick={() => handleDelete(code.id)} type="button">
              <S.Trash size={22} />
            </S.DeleteButton>
          </S.Item>
        ))}
      </S.BoxContainer>
    </S.Container>
  );
};
